import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CarFront, Cpu, FileSpreadsheet, LayoutGrid, Radar, Sparkles, Workflow } from 'lucide-react';
import './SampleTwoHome.css';

const AUTO_CREDIT_FORM_URL = 'https://form.jotform.com/251332360319045';

const serviceModules = [
  {
    title: 'Crédito Auto',
    description: 'Pré-análise direta com fluxo comercial orientado para velocidade e clareza.',
    href: AUTO_CREDIT_FORM_URL,
    external: true,
    icon: CarFront,
  },
  {
    title: 'Questionário Imóveis',
    description: 'Captação estruturada para casa, reforma e painel solar em etapas objetivas.',
    href: '/questionario-casas',
    icon: Building2,
  },
  {
    title: 'Gerar com IA',
    description: 'Descreva necessidades e avance rapidamente para um resultado mais tangível.',
    href: '/gerar-planta',
    icon: Sparkles,
  },
  {
    title: 'Editor 2D',
    description: 'Ferramenta central para transformar intenção comercial em proposta visual.',
    href: '/desenhar-planta',
    icon: LayoutGrid,
  },
];

const systemBlocks = [
  {
    title: 'Captação',
    text: 'Questionários e páginas curtas por vendedor, com leitura mais clara e cara de plataforma.',
    icon: Workflow,
  },
  {
    title: 'Organização',
    text: 'Módulos desenhados para transmitir estrutura, processo e tecnologia real.',
    icon: FileSpreadsheet,
  },
  {
    title: 'Visualização',
    text: 'IA, editor de planta e render 3D com linguagem mais corporativa e precisa.',
    icon: Radar,
  },
  {
    title: 'Escala',
    text: 'Uma direção visual mais próxima de startup, SaaS e operação profissional.',
    icon: Cpu,
  },
];

const SampleTwoHome = () => {
  return (
    <div className="sample-two-page" data-testid="sample-two-page">
      <div className="sample-two-topbar">
        <div className="sample-two-shell flex flex-wrap items-center justify-between gap-4 py-4">
          <div>
            <p className="sample-two-heading text-3xl tracking-[-0.04em]" data-testid="sample-two-brand-title">RH Platform</p>
            <p className="sample-two-mono text-xs uppercase tracking-[0.22em] text-[#404040]">Modelo 2 • PropTech Corporativo</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Link to="/" className="sample-two-button-secondary" data-testid="sample-two-open-sample-three-link">Sample 3</Link>
            <Link to="/sample-1" className="sample-two-button-secondary" data-testid="sample-two-open-sample-one-link">Sample 1</Link>
            <Link to="/sample-2" className="sample-two-button-primary" data-testid="sample-two-current-route-button">Sample 2</Link>
          </div>
        </div>
      </div>

      <main className="sample-two-shell pb-20">
        <section className="sample-two-hero">
          <div className="sample-two-grid">
            <div className="sample-two-panel p-8 sm:p-10">
              <p className="sample-two-mono text-xs uppercase tracking-[0.28em] text-[#1D3557]" data-testid="sample-two-hero-chip">plataforma • precisão • operação</p>
              <h1 className="sample-two-heading mt-6 text-5xl leading-[0.94] tracking-[-0.05em] sm:text-6xl" data-testid="sample-two-hero-title">
                Crédito, imóveis e tecnologia em uma única plataforma visual.
              </h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-[#404040]" data-testid="sample-two-hero-description">
                O Sample 2 traduz a RH Consulting como um ecossistema moderno, mais próximo de uma plataforma PropTech com estrutura, processo e velocidade comercial.
              </p>

              <div className="mt-8 flex flex-wrap gap-4">
                <a href={AUTO_CREDIT_FORM_URL} target="_blank" rel="noreferrer" className="sample-two-button-alert" data-testid="sample-two-hero-credit-link">
                  Crédito auto <ArrowRight size={16} />
                </a>
                <Link to="/desenhar-planta" className="sample-two-button-primary" data-testid="sample-two-hero-editor-link">
                  Abrir editor 2D
                </Link>
                <Link to="/questionario-casas" className="sample-two-button-secondary" data-testid="sample-two-hero-questionnaire-link">
                  Ver questionários
                </Link>
              </div>

              <div className="sample-two-grid-4 mt-10">
                {systemBlocks.map((block) => {
                  const Icon = block.icon;
                  return (
                    <div key={block.title} className="border border-[rgba(0,0,0,0.12)] bg-[#fafafa] p-5" data-testid={`sample-two-system-block-${block.title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}`}>
                      <Icon size={22} />
                      <p className="sample-two-mono mt-4 text-xs uppercase tracking-[0.18em] text-[#1D3557]">{block.title}</p>
                      <p className="mt-3 text-sm leading-7 text-[#404040]">{block.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sample-two-panel sample-two-blueprint min-h-[560px] p-8 sm:p-10">
              <p className="sample-two-mono text-xs uppercase tracking-[0.22em] text-[#1D3557]">módulos principais</p>
              <div className="mt-8 space-y-4">
                {serviceModules.map((module, index) => {
                  const Icon = module.icon;
                  const content = (
                    <div
                    key={`sample-two-module-content-${module.title}`}
                    className="flex items-start justify-between gap-4 border border-[rgba(0,0,0,0.12)] bg-white p-5 transition-transform duration-200 hover:-translate-y-1"
                  >
                      <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center border border-[rgba(0,0,0,0.12)] bg-[#f5f7fa] text-[#1D3557]">
                          <Icon size={22} />
                        </div>
                        <div>
                          <p className="sample-two-mono text-xs uppercase tracking-[0.18em] text-[#1D3557]">módulo {String(index + 1).padStart(2, '0')}</p>
                          <h2 className="sample-two-heading mt-2 text-3xl">{module.title}</h2>
                          <p className="mt-2 text-sm leading-7 text-[#404040]">{module.description}</p>
                        </div>
                      </div>
                      <ArrowRight size={18} />
                    </div>
                  );

                  if (module.external) {
                    return (
                      <a key={module.title} href={module.href} target="_blank" rel="noreferrer" data-testid={`sample-two-module-${module.title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}`}>
                        {content}
                      </a>
                    );
                  }

                  return (
                    <Link key={module.title} to={module.href} data-testid={`sample-two-module-${module.title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}`}>
                      {content}
                    </Link>
                  );
                })}
              </div>

              <div className="mt-8 border-t border-[rgba(0,0,0,0.12)] pt-6">
                <p className="sample-two-mono text-xs uppercase tracking-[0.18em] text-[#404040]">leitura visual</p>
                <p className="mt-3 max-w-xl text-sm leading-7 text-[#404040]" data-testid="sample-two-visual-description">
                  Este modelo fala com um público que valoriza organização, performance, clareza de processo e sensação de plataforma. É o caminho mais tech dos três.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="pt-10">
          <div className="sample-two-panel p-8 sm:p-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="sample-two-mono text-xs uppercase tracking-[0.24em] text-[#1D3557]" data-testid="sample-two-services-chip">sistema em blocos</p>
                <h2 className="sample-two-heading mt-4 text-5xl tracking-[-0.05em]" data-testid="sample-two-services-title">Como a plataforma se apresentaria.</h2>
              </div>
              <Link to="/sample-1" className="sample-two-button-secondary" data-testid="sample-two-compare-sample-one-button">
                Comparar com Sample 1
              </Link>
            </div>

            <div className="sample-two-grid-4 mt-8">
              {serviceModules.map((module) => {
                const Icon = module.icon;
                const inner = (
                  <>
                    <div
                      key={`sample-two-module-grid-icon-${module.title}`}
                      className="flex h-14 w-14 items-center justify-center border border-[rgba(0,0,0,0.12)] bg-[#f4f7fb] text-[#1D3557]"
                    >
                      <Icon size={24} />
                    </div>
                    <h3 className="sample-two-heading mt-6 text-3xl">{module.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-[#404040]">{module.description}</p>
                    <div className="sample-two-mono mt-6 text-xs uppercase tracking-[0.18em] text-[#E63946]">Acessar módulo</div>
                  </>
                );

                if (module.external) {
                  return (
                    <a key={module.title} href={module.href} target="_blank" rel="noreferrer" className="sample-two-panel p-7 transition-transform duration-200 hover:-translate-y-1" data-testid={`sample-two-service-card-${module.title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}`}>
                      {inner}
                    </a>
                  );
                }

                return (
                  <Link key={module.title} to={module.href} className="sample-two-panel p-7 transition-transform duration-200 hover:-translate-y-1" data-testid={`sample-two-service-card-${module.title.toLowerCase().replace(/[^a-z0-9]+/gi, '-')}`}>
                    {inner}
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="pt-10">
          <div className="sample-two-panel p-8 sm:p-10" data-testid="sample-two-final-cta">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              <div className="max-w-3xl">
                <p className="sample-two-mono text-xs uppercase tracking-[0.24em] text-[#1D3557]">comparação pronta</p>
                <h2 className="sample-two-heading mt-4 text-5xl tracking-[-0.05em]">Agora você pode comparar os 3 samples no navegador.</h2>
                <p className="mt-4 text-base leading-8 text-[#404040]">O Sample 2 fica entre a consultoria e a plataforma: mais técnico, mais corporativo e muito mais digital que o Sample 1.</p>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/sample-1" className="sample-two-button-secondary" data-testid="sample-two-open-sample-one-final-link">Abrir Sample 1</Link>
                <Link to="/" className="sample-two-button-secondary" data-testid="sample-two-open-sample-three-final-link">Abrir Sample 3</Link>
                <Link to="/sample-2" className="sample-two-button-primary" data-testid="sample-two-open-sample-two-final-link">Ficar no Sample 2</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SampleTwoHome;